<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppIcon from '../components/AppIcon.vue'
import SiteFooter from '../components/SiteFooter.vue'
import SiteHeader from '../components/SiteHeader.vue'
import { ApiError, apiRequest } from '../services/api'

interface Team { id: string; name: string; member_count: number; open_join: number; captain_player_id: string }
interface Member { id: string; display_name: string }
interface JoinRequest { id: string; player_id: string; display_name: string }
interface State {
  player: { id: string; team_id: string | null; display_name: string }
  session: { phase: string; answer_close_at: string | null }
  teams: Team[]
  teamMembers: Member[]
  joinRequests: JoinRequest[]
  myJoinRequest: { id: string; status: string; team_name: string } | null
  question: { id: string; prompt: string; image_url: string | null; type: string; choices: Array<{ id: string; label?: string; imageUrl?: string }> | null } | null
  submission: { raw_answer: string } | null
  standings: Array<{ id: string; name: string; score: number }>
  reveal: { groups: Array<{ canonical_answer: string; rank: number; final_count: number }>; pointsEarned: number } | null
}

const route = useRoute()
const sessionId = String(route.params.sessionId)
const credential = localStorage.getItem(`crowd-control-player:${sessionId}`) ?? ''
const state = ref<State | null>(null)
const teamName = ref('')
const error = ref('')
const busy = ref(false)
const answer = ref('')
let socket: WebSocket | null = null
let reconnectTimer: number | undefined
let stopped = false
const currentTeam = computed(() => state.value?.teams.find((team) => team.id === state.value?.player.team_id))
const isCaptain = computed(() => currentTeam.value?.captain_player_id === state.value?.player.id)
const phaseLabel = computed(() => (state.value?.session.phase ?? 'lobby').replaceAll('_', ' ').toLowerCase())

// A new question must never start with the previous answer still in the box.
watch(() => state.value?.question?.id, () => { answer.value = '' })

function playerRequest<T>(path: string, init?: Parameters<typeof fetch>[1]): Promise<T> {
  return apiRequest<T>(path, { ...init, headers: { ...init?.headers, authorization: `Bearer ${credential}` } })
}

async function refresh(): Promise<void> {
  state.value = await playerRequest<State>('/api/player/session')
}

async function run(action: () => Promise<void>): Promise<void> {
  error.value = ''; busy.value = true
  try { await action(); await refresh() } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'The request could not be completed.'
  } finally { busy.value = false }
}

onMounted(async () => {
  if (!credential) { error.value = 'Your player credential is missing. Rejoin the game.'; return }
  await run(async () => undefined)
  connect()
})

function connect(): void {
  if (stopped || !credential) return
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  socket = new WebSocket(`${protocol}//${location.host}/api/player/session/socket`, ['crowd-control', credential])
  socket.addEventListener('message', () => { void refresh() })
  socket.addEventListener('close', () => {
    if (!stopped) reconnectTimer = window.setTimeout(connect, 1500)
  })
}

onBeforeUnmount(() => {
  stopped = true
  if (reconnectTimer) window.clearTimeout(reconnectTimer)
  socket?.close(1000, 'Leaving page')
})

async function create(solo: boolean): Promise<void> {
  await run(async () => {
    const name = solo ? state.value?.player.display_name : teamName.value
    await playerRequest('/api/player/teams', { method: 'POST', body: JSON.stringify({ name, solo }) })
  })
}

async function requestTeam(teamId: string): Promise<void> {
  await run(async () => {
    await playerRequest('/api/player/team/request', { method: 'POST', body: JSON.stringify({ teamId }) })
  })
}

async function resolve(requestId: string, allow: boolean): Promise<void> {
  await run(() => playerRequest('/api/player/team/request/resolve', {
    method: 'POST', body: JSON.stringify({ requestId, allow }),
  }))
}

async function setAutoAccept(enabled: boolean): Promise<void> {
  await run(() => playerRequest('/api/player/team/auto-accept', {
    method: 'POST', body: JSON.stringify({ enabled }),
  }))
}

async function transfer(playerId: string): Promise<void> {
  await run(() => playerRequest('/api/player/team/captain', {
    method: 'POST', body: JSON.stringify({ playerId }),
  }))
}

async function removeMember(playerId: string): Promise<void> {
  await run(() => playerRequest('/api/player/team/member/remove', {
    method: 'POST', body: JSON.stringify({ playerId }),
  }))
}

async function submit(): Promise<void> {
  await run(() => playerRequest('/api/player/answer', { method: 'POST', body: JSON.stringify({ answer: String(answer.value) }) }))
}

async function submitChoice(choiceId: string): Promise<void> {
  answer.value = choiceId
  await submit()
}
</script>

<template>
  <div class="page-wrapper">
    <SiteHeader minimal />
    <main class="section section--top">
      <div class="container container--narrow">
        <section
          v-reveal
          class="panel panel--pad stack stack--lg"
        >
          <div class="row-flex row-flex--between">
            <span class="sub-title"><strong>Player</strong> gamecard</span>
            <span
              v-if="state"
              class="tag tag--primary"
            >{{ phaseLabel }}</span>
          </div>
          <p
            v-if="error"
            class="alert"
            role="alert"
          >
            {{ error }}
          </p>
          <p v-if="!state && !error">
            Restoring your game…
          </p>

          <!-- on a team -->
          <div
            v-else-if="state?.player.team_id"
            class="stack stack--lg"
          >
            <template v-if="state.session.phase === 'QUESTION_OPEN' && state.question">
              <h2 class="fw-6">
                {{ state.question.prompt }}
              </h2>
              <img
                v-if="state.question.image_url"
                class="question-image"
                :src="state.question.image_url"
                alt=""
              >
              <p
                v-if="state.submission"
                class="answer-locked"
              >
                Your team locked in: <strong>{{ state.submission.raw_answer }}</strong>
              </p>
              <div
                v-else-if="state.question.type.startsWith('MULTIPLE_CHOICE')"
                class="choice-grid"
              >
                <button
                  v-for="choice in state.question.choices"
                  :key="choice.id"
                  class="choice-button"
                  type="button"
                  :disabled="busy"
                  @click="submitChoice(choice.id)"
                >
                  <img
                    v-if="choice.imageUrl"
                    :src="choice.imageUrl"
                    alt=""
                  >
                  <span v-if="choice.label">{{ choice.label }}</span>
                </button>
              </div>
              <form
                v-else
                class="stack"
                @submit.prevent="submit"
              >
                <div class="input-wrapper">
                  <label for="answer">{{ state.question.type.startsWith('NUMERIC') ? 'Your number' : 'Your team’s answer' }}</label>
                  <div class="input-single">
                    <AppIcon :name="state.question.type.startsWith('NUMERIC') ? 'hash' : 'message'" />
                    <input
                      id="answer"
                      v-model="answer"
                      :type="state.question.type.startsWith('NUMERIC') ? 'number' : 'text'"
                      :inputmode="state.question.type.startsWith('NUMERIC') ? 'decimal' : 'text'"
                      :step="state.question.type.startsWith('NUMERIC') ? 'any' : undefined"
                      maxlength="120"
                      autocomplete="off"
                      required
                    >
                  </div>
                </div>
                <button
                  type="submit"
                  class="btn--primary btn--block"
                  :disabled="busy || !String(answer).trim()"
                >
                  <AppIcon name="lock" /> Lock answer
                </button>
              </form>
            </template>

            <template v-else-if="['REVEALED', 'STANDINGS', 'COMPLETE'].includes(state.session.phase)">
              <section
                v-if="state.reveal"
                class="stack"
              >
                <h2 class="fw-6">
                  Crowd results
                </h2>
                <p v-if="state.submission">
                  Your team answered: <strong class="primary-text">{{ state.submission.raw_answer }}</strong>
                </p>
                <p class="text-xl">
                  <strong class="secondary-text">{{ state.reveal.pointsEarned }}</strong> points earned
                </p>
                <ol class="reveal-list">
                  <li
                    v-for="group in state.reveal.groups"
                    :key="`${group.rank}-${group.canonical_answer}`"
                  >
                    <strong><span
                      class="avatar"
                      :class="{ 'avatar--secondary': group.rank === 1 }"
                    >{{ group.rank }}</span>#{{ group.rank }} {{ group.canonical_answer }}</strong><span>{{ group.final_count }} teams</span>
                  </li>
                </ol>
              </section>
              <h2 class="fw-6">
                {{ state.session.phase === 'COMPLETE' ? 'Final standings' : 'Standings' }}
              </h2>
              <ol class="standings-list">
                <li
                  v-for="(team, index) in state.standings"
                  :key="team.id"
                >
                  <strong><span
                    class="avatar"
                    :class="{ 'avatar--secondary': index === 0 }"
                  >{{ index + 1 }}</span>{{ index + 1 }}. {{ team.name }}</strong><span>{{ team.score }} points</span>
                </li>
              </ol>
            </template>

            <template v-else>
              <div>
                <h2 class="fw-6">
                  {{ currentTeam?.name }}
                </h2>
                <p class="muted-text mt-8">
                  {{ state.session.phase === 'LOBBY' ? 'Waiting for the host…' : 'Answer locked. Waiting for the host…' }}
                </p>
              </div>
            </template>

            <div
              v-if="state.session.phase === 'LOBBY'"
              class="stack stack--lg"
            >
              <template v-if="isCaptain">
                <hr class="divider divider--dashed">
                <h3 class="text-xl fw-6">
                  Captain controls
                </h3>
                <label class="check-row"><input
                  :checked="Boolean(currentTeam?.open_join)"
                  type="checkbox"
                  @change="setAutoAccept(($event.target as HTMLInputElement).checked)"
                > Auto Accept new members</label>
                <article
                  v-for="request in state.joinRequests"
                  :key="request.id"
                  class="list-card"
                >
                  <div class="row-flex row-flex--between">
                    <strong>{{ request.display_name }}</strong><span class="muted-text">wants to join</span>
                  </div>
                  <div class="button-row">
                    <button
                      type="button"
                      class="btn--primary btn--sm"
                      @click="resolve(request.id, true)"
                    >
                      Allow
                    </button>
                    <button
                      class="btn--danger btn--sm"
                      type="button"
                      @click="resolve(request.id, false)"
                    >
                      Deny
                    </button>
                  </div>
                </article>
                <h3 class="text-xl fw-6">
                  Team members
                </h3>
                <article
                  v-for="member in state.teamMembers"
                  :key="member.id"
                  class="list-card"
                >
                  <strong>{{ member.display_name }}</strong>
                  <div
                    v-if="member.id !== state.player.id"
                    class="button-row"
                  >
                    <button
                      type="button"
                      class="btn--ghost btn--sm"
                      @click="transfer(member.id)"
                    >
                      <AppIcon
                        name="crown"
                        :size="18"
                      /> Make captain
                    </button>
                    <button
                      class="btn--danger btn--sm"
                      type="button"
                      @click="removeMember(member.id)"
                    >
                      Remove
                    </button>
                  </div>
                </article>
              </template>
            </div>
          </div>

          <!-- choosing a team -->
          <div
            v-else-if="state"
            class="stack stack--lg"
          >
            <h2 class="fw-6">
              Choose how to play
            </h2>
            <p
              v-if="state.myJoinRequest?.status === 'PENDING'"
              class="notice"
            >
              Waiting for {{ state.myJoinRequest.team_name }}’s captain…
            </p>
            <div class="input-wrapper">
              <label for="team-name">Team name</label>
              <div class="input-single">
                <AppIcon name="users" />
                <input
                  id="team-name"
                  v-model="teamName"
                  minlength="2"
                  maxlength="40"
                  placeholder="Team name"
                >
              </div>
            </div>
            <div class="button-row">
              <button
                type="button"
                class="btn--primary"
                :disabled="busy || teamName.length < 2"
                @click="create(false)"
              >
                Create team <AppIcon name="arrow-right" />
              </button>
              <button
                type="button"
                class="btn--secondary"
                :disabled="busy"
                @click="create(true)"
              >
                Play solo
              </button>
            </div>
            <hr class="divider divider--dashed">
            <h2 class="text-xxl fw-6">
              Join an existing team
            </h2>
            <p
              v-if="!state.teams.length"
              class="muted-text"
            >
              No teams yet. Be the first to create one.
            </p>
            <article
              v-for="team in state.teams"
              :key="team.id"
              class="list-card"
            >
              <div class="row-flex row-flex--between">
                <strong>{{ team.name }}</strong><span class="tag"><AppIcon
                  name="users"
                  :size="16"
                /> {{ team.member_count }} players</span>
              </div>
              <div>
                <button
                  type="button"
                  class="btn--secondary btn--sm"
                  :disabled="busy"
                  @click="requestTeam(team.id)"
                >
                  {{ team.open_join ? 'Join' : 'Request to join' }}
                </button>
              </div>
            </article>
          </div>
        </section>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
